import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "../config/axiosConfig";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";

//Hook para cacheLoader
export const fetchTerceros = async (id_empresa) => {
  if (!id_empresa) {
    console.warn("⚠️ `id_empresa` no es válido en fetchTerceros:", id_empresa);
    return [];
  }

  try {
    const response = await api.get(`/${id_empresa}/tercero`);
    return response.data;
  } catch (error) {
    console.error("❌ Error en la petición:", error);
    return [];
  }
};

// Hook reutilizable con React Query
export const useTerceros = () => {
  const { empresaSeleccionada } = useSelector((state) => state.auth);
  const id_empresa = empresaSeleccionada?.id; // ✅ Asegurar que existe un `id_empresa`

  return useQuery({
    queryKey: ["terceros", id_empresa],
    queryFn: () => (id_empresa ? fetchTerceros(id_empresa) : Promise.resolve([])),
    enabled: !!id_empresa, // 🔹 Habilitar la caché solo si hay una empresa seleccionada
  });
};

// Función para guardar un nuevo tercero (POST)
export const saveTercero = async (terceroData, id_empresa) => {
  if (!id_empresa) throw new Error("⚠️ `id_empresa` es obligatorio para guardar un tercero.");

  const response = await api.post(`/${id_empresa}/tercero`, terceroData); // ✅ Ahora incluye `id_empresa`
  return response.data;
};

// Hook para manejar la mutación (guardar un nuevo tercero)
export const useSaveTercero = () => {
  const queryClient = useQueryClient();
  const { empresaSeleccionada } = useSelector((state) => state.auth);
  const id_empresa = empresaSeleccionada?.id;

  return useMutation({
    mutationFn: (terceroData) => saveTercero(terceroData, id_empresa),
    onSuccess: (newTercero) => {
      queryClient.setQueryData(["terceros", id_empresa], (oldData) => {
        if (!oldData) return [newTercero]; // Si no hay datos previos, iniciar con este

        // Verificar si ya existe el tercero en la lista
        const exists = oldData.some((tercero) => tercero.id === newTercero.id);
        if (exists) return oldData; // No hacer nada si ya está en la lista

        return [...oldData, newTercero]; // Agregar el nuevo tercero
      });
    },
  });
};

export const useUpdateTercero = () => {
  const queryClient = useQueryClient();
  const { empresaSeleccionada } = useSelector((state) => state.auth);
  const id_empresa = empresaSeleccionada?.id;

  return useMutation({
    mutationFn: async (tercero) => {
      if (!id_empresa) throw new Error("⚠️ `id_empresa` es obligatorio para actualizar un tercero.");
      if (!tercero?.id) throw new Error("⚠️ `id` del tercero es obligatorio para actualizar.");
      const response = await api.put(`/${id_empresa}/tercero/${tercero.id}`, tercero); // ✅ Incluye `id_empresa`
      return response.data;
    },
    onSuccess: (newTercero) => {
      // queryClient.invalidateQueries(["terceros", id_empresa]); // ✅ Refrescar caché de la empresa correcta
      queryClient.setQueryData(["terceros", id_empresa], (oldData) => {
        if (!oldData) return [newTercero]; // Si no hay datos previos, usa el nuevo
        return oldData.map((tercero) => (tercero.id === newTercero.id ? newTercero : tercero));
      });
      console.log("✅ Tercero actualizado en cache:", newTercero);
    },
  });
};

export const useDeleteTercero = () => {
  const queryClient = useQueryClient();
  const { empresaSeleccionada } = useSelector((state) => state.auth);
  const id_empresa = empresaSeleccionada?.id;

  return useMutation({
    mutationFn: async (ids) => {
      if (!id_empresa) throw new Error("⚠️ `id_empresa` es obligatorio para eliminar un tercero.");
      if (!ids || (Array.isArray(ids) && ids.length === 0)) throw new Error("⚠️ Se requiere al menos un `id_tercero`.");

      const idList = Array.isArray(ids) ? ids : [ids];
      console.log("⚠️ Eliminando terceros:", idList);

      const loadingToastId = toast.loading("Eliminando terceros... 🔄");

      // 🔥 Traer los terceros del cache ANTES de eliminar
      const tercerosCache = queryClient.getQueryData(["terceros", id_empresa]) || [];

      const results = await Promise.allSettled(idList.map((id) => api.delete(`/${id_empresa}/tercero/${id}`)));

      toast.dismiss(loadingToastId); // Cerrar el loading

      const deletedIds = [];
      const failedIds = [];

      results.forEach((res, index) => {
        const idTercero = idList[index];
        const tercero = tercerosCache.find((t) => t.id === idTercero);
        const nombreTercero = tercero?.nombre || `ID ${idTercero}`;

        if (res.status === "fulfilled") {
          deletedIds.push(idTercero);
          toast.success(`✅ Tercero "${nombreTercero}" eliminado.`);
        } else {
          failedIds.push(idTercero);
          toast.warning(`⚠️ No se pudo eliminar el tercero "${nombreTercero}" (comprobantes asociados).`);
        }
      });

      queryClient.setQueryData(["terceros", id_empresa], (oldData) => {
        if (!oldData) return [];
        return oldData.filter((tercero) => !deletedIds.includes(tercero.id));
      });

      return { deletedIds, failedIds };
    },
  });
};
